import { useEffect, useState } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import type { ConnectionStatus } from '../../lib/messageSource'

/** A blip shorter than this is not worth a word on screen. */
const SHOW_AFTER_MS = 3000
/** How long "Connected" holds once the link is back. */
const BACK_FOR_MS = 1600

/** Two halves of a chain link: apart and searching while the link is down,
 *  joined once it is back. */
function LinkIcon({ searching }: { searching: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-[1.3em] w-[1.3em] shrink-0" strokeWidth={2.2} strokeLinecap="round">
      <g className={searching ? 'motion-safe:animate-link-left' : ''}>
        <path d="M10.5 13.5 8 16a3.2 3.2 0 0 1-4.5-4.5L6 9" stroke="currentColor" />
        <path d="M9 15l2-2" stroke="currentColor" />
      </g>
      <g className={searching ? 'motion-safe:animate-link-right' : ''}>
        <path d="M13.5 10.5 16 8a3.2 3.2 0 0 1 4.5 4.5L18 15" stroke="currentColor" />
        <path d="M15 9l-2 2" stroke="currentColor" />
      </g>
    </svg>
  )
}

/**
 * The link to the till, when it matters: after a few seconds down, a light
 * sweeps the top of the order and "Reconnecting" shows with the link icon
 * searching; when it comes back the icon joins and "Connected" holds for a
 * moment. Short blips pass without a word, so an order that has stopped
 * updating never looks live, and a healthy one is never cluttered.
 */
export default function ConnectionMark({ status }: { status: ConnectionStatus }) {
  const { t } = useTranslation()
  // `late`: down long enough to mention. `back`: just recovered from that.
  const [late, setLate] = useState(false)
  const [back, setBack] = useState(false)
  const [previous, setPrevious] = useState(status)
  if (status !== previous) {
    setPrevious(status)
    if (status === 'online') {
      setBack(late)
      setLate(false)
    } else {
      setBack(false)
    }
  }

  useEffect(() => {
    if (status !== 'offline') return
    const timer = setTimeout(() => setLate(true), SHOW_AFTER_MS)
    return () => clearTimeout(timer)
  }, [status])

  useEffect(() => {
    if (!back) return
    const timer = setTimeout(() => setBack(false), BACK_FOR_MS)
    return () => clearTimeout(timer)
  }, [back])

  const searching = status === 'offline' && late
  if (!searching && !back) return null

  return (
    <>
      {searching && (
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[3px] overflow-hidden bg-trust-100">
          <div className="h-full w-1/4 bg-gradient-to-r from-transparent via-trust-500 to-transparent motion-safe:animate-seek" />
        </div>
      )}
      <div
        role="status"
        className={`ml-auto flex motion-safe:animate-itemIn items-center space-x-2 rounded-full px-[0.9em] py-[0.3em] text-cfd-label ${
          searching ? 'bg-trust-50 text-trust-700' : 'bg-success/10 text-success'
        }`}
      >
        <LinkIcon searching={searching} />
        <span>{searching ? t('reconnecting') : t('connected')}</span>
      </div>
    </>
  )
}
